import { ActionType } from '../infrastructure/actionTypes';
import { ICrop, IDocument, IPage, IEditorState } from '../infrastructure/interfaces';
import { Steps } from '../infrastructure/Steps';
import DocumentService from '../services/DocumentService';
import { mapPagesToSpreads, simplePage, reorderPages, movePages } from '../services/PageService';
import { LayoutType } from 'src/infrastructure/LayoutTypes';
import { placeholderTypes } from 'src/infrastructure/placeholderTypes'
import { PageType } from 'src/infrastructure/PageType';
import { productType } from 'src/infrastructure/ProductType';

const agronomyExtrasLayoutCount: number = 2;
const agronomyTableLayoutCount: number = 16;
const multiLayoutCount: number = 6;

const getNumberOfPlaceholders = (documentType: string, layoutType: LayoutType | null, isLeftPage: Boolean) : number => {

  if (documentType === productType.Brochure) {
    return 4;
  }

  switch (layoutType) {
    case LayoutType.Agronomy:
      return isLeftPage ? (agronomyTableLayoutCount + agronomyExtrasLayoutCount) : agronomyExtrasLayoutCount;
    case LayoutType.MultiLayout:
      return 6;
    case LayoutType.HybridLayout:
      return isLeftPage ? multiLayoutCount : 1;
    default:
      return 1;
  }
};

const insertAndReturnPages = (documentType: string, pages: Array<IPage>, pagesCount: number, editor: IEditorState, layoutFlag: LayoutType | null) => {
  let _pagesCount = pagesCount;
  let insertIndex = 0;
  if (editor
      && (editor.selectedSpread != null)
      && (editor.selectedPage != null)
      && (editor.selectedSpread.right != null)
      && (editor.selectedPage.type === PageType.Page)) {
    insertIndex = editor.selectedSpread.right.number;
  }
 
  if (documentType === productType.Bulletin) {
    _pagesCount = 1;
  }

  let newPagesArray: Array<IPage> = [];
  const coverPages = [pages[0], pages[pages.length - 1]];

  let mainPages = pages.filter((page) => {
    return page.type ===PageType.Page;
  });

  if (pages.length === _pagesCount) {
    newPagesArray = pages;
  } else if (pages.length < _pagesCount) {
    let iterator = insertIndex === 0 ? 0 : insertIndex - 1;

    for (let i = 0; i < _pagesCount - pages.length; i++) {

      let placeholdersCount = getNumberOfPlaceholders(documentType, layoutFlag, (iterator % 2 === 0));
      mainPages.splice(iterator, 0, simplePage(iterator + 2, placeholdersCount));
      iterator = iterator + 1;
    }

    //If the call is coming from "Add Multi Placeholder Page" Button in Catalog, flag the pages so they have a "MultiPlaceholder"
    if (documentType !== productType.Bulletin && ((layoutFlag === LayoutType.MultiLayout) || (layoutFlag === LayoutType.HybridLayout))) {
      mainPages = mainPages.map((page, key) => {
        // Mark 6 item placeholder pages as being "MultiLayout"
        if (page.placeholders.length === multiLayoutCount) {
          page.layout = LayoutType.MultiLayout;
        }
        return page;
      });
    } else 
    //If the call is coming from "Add Agronomy Spread" Button in Catalog, flag the pages so they have a "Agronomy" layout
    if(documentType !== productType.Bulletin && layoutFlag === LayoutType.Agronomy) {
      mainPages = mainPages.map((page, key) => {
        if(page.layout !== LayoutType.Agronomy && (page.placeholders.length === (agronomyTableLayoutCount + agronomyExtrasLayoutCount) || page.placeholders.length === agronomyExtrasLayoutCount)){
          page.layout = LayoutType.Agronomy;
          page.placeholders.map((placeholder, key) => {
            // Only the first 16 placeholders of the left page are the agronomy chart
            let isItemInAgronomyTable = (page.placeholders.length === (agronomyTableLayoutCount + agronomyExtrasLayoutCount) && key < agronomyTableLayoutCount);
            placeholder.type = isItemInAgronomyTable ? placeholderTypes.Agronomy : placeholderTypes.Seed;
          });
        }
        return page;
      });
    }
    
    // This method sets the page "titles" in JSON too
    newPagesArray = reorderPages(mainPages);
    newPagesArray.unshift(coverPages[0]);
    newPagesArray.push(coverPages[1]);
  } else if (pages.length > _pagesCount) {
    newPagesArray = mainPages.splice(0, _pagesCount - 2);
    newPagesArray.unshift(coverPages[0]);
    newPagesArray.push(coverPages[1]);
  }
  return newPagesArray;
};

export default (document: IDocument | null, action: any) => {
  switch (action.type) {
    case ActionType.CREATE_DOCUMENT:
      {
        const { prodType, pagesCount } = action;
        const newDocument = DocumentService.getDocument(prodType, pagesCount);

        return { ...newDocument };
      }
    case ActionType.CHANGE_DOCUMENT_PAGES_COUNT:
      {
        let newDocument;
        const currentDocType = (document != null) ? document.type : null;
        const docType = action.docType || currentDocType;
        const currentDocPages = (document != null) ? document.pages : null;
        const pages = insertAndReturnPages(docType, (action.document && action.document.pages) || currentDocPages, action.pagesCount, action.editor, LayoutType.FullPage);
        const spreads = mapPagesToSpreads(pages, docType);
        newDocument = {
          ...document,
          pages,
          spreads,
        };
        return newDocument;
      }
      case ActionType.CHANGE_DOCUMENT_PAGES_COUNT_MULTI_LAYOUT:
      {
        let newDocument;
        const currentDocType = (document != null) ? document.type : null;
        const docType = action.docType || currentDocType;
        const currentDocPages = (document != null) ? document.pages : null;
        const pages = insertAndReturnPages(docType, (action.document && action.document.pages) || currentDocPages, action.pagesCount, action.editor, LayoutType.MultiLayout);
        
        const spreads = mapPagesToSpreads(pages, docType);
        newDocument = {
          ...document,
          pages,
          spreads,
        };
        return newDocument;
      }
      case ActionType.CHANGE_DOCUMENT_PAGES_COUNT_AGRONOMY_LAYOUT:
      {
        let newDocument;
        const currentDocType = (document != null) ? document.type : null;
        const docType = action.docType || currentDocType;
        const currentDocPages = (document != null) ? document.pages : null;
        const pages = insertAndReturnPages(docType, (action.document && action.document.pages) || currentDocPages, action.pagesCount, action.editor, LayoutType.Agronomy);
        const spreads = mapPagesToSpreads(pages, docType);
        newDocument = {
          ...document,
          pages,
          spreads,
        };
        return newDocument;
      }
      case ActionType.CHANGE_DOCUMENT_PAGES_COUNT_MIXED_LAYOUT:
        {
          let newDocument;
          const currentDocType = (document != null) ? document.type : null;
          const docType = action.docType || currentDocType;
          const currentDocPages = (document != null) ? document.pages : null;
          const pages = insertAndReturnPages(docType, (action.document && action.document.pages) || currentDocPages, action.pagesCount, action.editor, LayoutType.HybridLayout);

          const spreads = mapPagesToSpreads(pages, docType);
          newDocument = {
            ...document,
            pages,
            spreads,
          };
          return newDocument;
        }
    case ActionType.TOGGLE_CROP:
      {
        const crop = action.crop;
        const documentCrops = (document!= null) ? document.crops : [];
        let crops = [...documentCrops];
        if (crops.find(c => c.id === crop.id)) {
          crops = crops.filter(c => c.id !== crop.id);
        } else {
          crops.push(crop);
        }

        return {
          ...document,
          crops,
        };
      }
    case ActionType.TOGGLE_BRANDING:
      {
        const branding = action.branding;
        const documentBrandings = (document!= null) ? document.brandings : [];
        let brandings = [...documentBrandings];
        if (brandings.find(c => c.id === branding.id)) {
          brandings = brandings.filter(c => c.id !== branding.id);
        } else {
          brandings.push(branding);
        }

        return {
          ...document,
          brandings,
        };
      }
    case ActionType.MOVE_CROP: {
      const {
        dragIndex,
        hoverIndex,
      } = action;

      const documentCrops = (document!= null) ? document.crops : [];
      const crops = [...documentCrops];

      const dragCrop = crops[dragIndex];
      const hoverCrop = crops[hoverIndex];

      const indexDrag = crops.indexOf(dragCrop);
      const indexHover = crops.indexOf(hoverCrop);

      // swap crops via dragging
      crops[indexDrag] = hoverCrop;
      crops[indexHover] = dragCrop;

      return {
        ...document,
        crops,
      };
    }
    case ActionType.CHANGE_CROP_IMAGE:
      {
        const defaultImage = action.image;
        const crop = action.crop;
        const documentCrops = (document!= null) ? document.crops : [];
        const crops: ICrop[] = documentCrops.map(c =>
          c.id === crop.id
            ? { ...c, defaultImage }
            : c);
        return {
          ...document,
          crops,
        };
      }
    case ActionType.CHANGE_POSTCARD_IMAGE_FRONT:
      {
        const { postcardImageFront } = action;
        return {
          ...document,
          postcardImageFront,
        };
      }
    case ActionType.CHANGE_POSTER_IMAGE_BACKGROUND:
      {
        const { posterImageBackground } = action;
        return {
          ...document,
          posterImageBackground,
        };
      }
    case ActionType.CHANGE_POSTCARD_IMAGE_BACK:
      {
        const { postcardImageBack } = action;
        return {
          ...document,
          postcardImageBack,
        };
      }
    case ActionType.CHANGE_BRANDING:
      {
        const { branding } = action;
        return {
          ...document,
          branding,
        };
      }
    case ActionType.CHANGE_COMPANY_INFO:
    {
      const { text, field } = action;
      const companyInfo = (document != null) ?
      document.companyInfo : 
      {
        isEnabled: false,
        divisionName: "",
        phoneNumber: "",
        email: ""
      };

      return {
        ...document,
        companyInfo: {
          ...companyInfo,
          [field]: text,
        },
      };
    }
    case ActionType.CHANGE_COVER_IMAGE:
      {
        const { coverImage } = action;
        return {
          ...document,
          coverImage,
        };
      }
    case ActionType.CHANGE_DOCUMENT_TITLE:
      {
        const { title } = action;
        return {
          ...document,
          title,
        };
      }

    case ActionType.CHANGE_ADDITIONAL_INFO:
      {
        const { text, field } = action;
        const documentAdditionalInfo = (document != null) ?
          document.additionalInfo :
          {
            text: "",
            isEnabled: false,
            date: new Date()
          };
        return {
          ...document,
          additionalInfo: {
            ...documentAdditionalInfo,
            [field]: text,
          },
        };
      }
    case ActionType.TOGGLE_LOGO:
      {
        const logo = action.logo;
        const documentLogos = (document!= null) ? document.logos : [];
        let logos = [...documentLogos];
        if (logos.findIndex(l => l.id === logo.id) > -1) {
          logos = logos.filter(l => l.id !== logo.id);
        } else {
          logos.push(logo);
        }

        return {
          ...document,
          logos,
        };
      }
    case ActionType.OPEN_DOCUMENT_DONE:
      {
        return action.document;
      }
    case ActionType.SAVE_DOCUMENT:
      {
        const { pages, spreads } = action.document;
        return {
          ...action.document,
          pages,
          spreads,
        };
      }
    case ActionType.SKIP_STEP:
      {
        const { step } = action;
        switch (step) {
          case Steps.CropsChoosing:
            {
              return { ...document, crops: [] };
            }
          case Steps.BrandingsChoosing:
            {
              return document;
            }
          case Steps.CoverImagesChoosing:
            {
              return document;
            }
          case Steps.LogosChoosing:
            {
              return { ...document, logos: [] };
            }
          case Steps.CompanyInfoChoosing:
            {
              return {
                ...document,
                companyInfo: {
                  divisionName: '',
                  phoneNumber: '',
                  isEnabled: true,
                },
              };
            }
          default:
            {
              return document;
            }
        }
      }
    case ActionType.UPDATE_BRANDINGS:
      const { brandings } = action;
      return { ...document, brandings };
    case ActionType.WRITE_SPREAD_TO_DOCUMENT:
      return document;
    case ActionType.WRITE_PAGE_TO_DOCUMENT:
      {
        const documentPages = (document!= null) ? document.pages : [];
        const pages = documentPages.map((item) => {
          let page;
          if (item.number === action.page.number) {
            page = action.page;
          } else {
            page = item;
          }
          return page;
        });
        return {
          ...document,
          pages,
        };
      }
    case ActionType.DRAG_N_DROP_PRODUCT:
      {
        if (document == null) {
          return document;
        }
        return DocumentService.dranNDropHandler(document, action);
      }
    case ActionType.REMOVE_PRODUCT:
      {
        if (document == null) {
          return document;
        }
        return DocumentService.removeProductHandler(document, action);
      }
    case ActionType.ON_MOVE_PLACEHOLDERS:
      {
        if (document == null) {
          return document;
        }
        return DocumentService.movePlaceholderHandler(document, action);
      }
    case ActionType.ON_ADD_PAGE_OR_SPREAD:
      {
        return document;
      }
    case ActionType.ON_REMOVE_SPREAD:
      {
        if (document == null) {
          return document;
        }

        let newPages: Array<IPage> = [];

        document.spreads.filter((spread) => {
          if (spread.id === action.spreadId) {
            newPages = document.pages.filter((page, key) => {
              return page.number !== spread.left.number && (!spread.right || page.number !== spread.right.number);
            });
          }
          return spread.id !== action.spreadId;
        });
        newPages = newPages.map((page, key) => {
          const iterator = key + 1;
          page.number = iterator;
          if (page.type === 2) {
            page.title = 'Page' + iterator;
          }
          return page;
        });
        const newSpreads = mapPagesToSpreads(newPages, action.docType);
        return {
          ...document,
          spreads: newSpreads,
          pages: newPages,
        };
      }
    case ActionType.ON_REMOVE_PAGE:
      {
        if (document == null) {
          return document;
        }

        let newPages = document.pages.filter((page) => {
          return action.pageId !== page.number;
        });
        newPages = newPages.map((page, key) => {
          const iterator = key + 1;
          page.number = iterator;
          if (page.type === 2) {
            if (iterator % 2 === 0) {
              page.position = 'left';
            } else {
              page.position = 'right';
            }
            page.title = 'Page' + iterator;
          }
          return page;
        });
        const newSpreads = mapPagesToSpreads(newPages, action.docType);
        return {
          ...document,
          spreads: newSpreads,
          pages: newPages,
        };
      }
    case ActionType.MOVE_THUMBNAIL:
      {
        if (document == null) {
          return document;
        }
        
        const {
          dragIndex,
          hoverIndex,
        } = action;

        const docType = document.type;
        const pages = movePages(document.pages, dragIndex, hoverIndex, action.editor);
        const spreads = mapPagesToSpreads(pages, docType);
        const newDocument = {
          ...document,
          pages,
          spreads,
        };

        return newDocument;
      }
    case ActionType.SAVE_DOCUMENT_DONE:
    {
      return action.document;
    }
    default:
      if (document == null) {
        return {};
      }
      return document;
  }
};



// WEBPACK FOOTER //
// ./src/reducers/document.ts